import { useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import UserChatBubble from "../../Forms/FirmUserBtn";

const sameId = (a, b) =>
  a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

const splitName = (name = "") => {
  const [first = "", ...rest] = String(name).trim().split(/\s+/);
  return { first, last: rest.join(" ") };
};

/**
 * Group members' names. `group_member_avatars` may only carry `id` and `avatar_url`, so names come
 * from the member itself when present, then the firm chat's user list, then (for yourself) the
 * signed-in user. `null` means the name is unknown.
 */
const useMemberNames = (members) => {
  const chatUsers = useSelector((state) => state.chat2?.users);
  const currentUser = useSelector((state) => state.chat2?.currentUser);
  const selfId = localStorage.getItem("loggedInUser");
  return members.map((member) => {
    if (member?.first_name || member?.last_name) {
      return { first: member.first_name || "", last: member.last_name || "" };
    }
    const ownName = member?.name || member?.display_name || member?.full_name;
    if (ownName) return splitName(ownName);
    const thread = (chatUsers || []).find(
      (item) => item?.thread_type !== "group" && sameId(item?.other_participant_id, member?.id)
    );
    if (thread?.first_name || thread?.last_name) {
      return { first: thread.first_name || "", last: thread.last_name || "" };
    }
    if (sameId(member?.id, selfId)) {
      if (currentUser?.first_name || currentUser?.last_name) {
        return { first: currentUser.first_name || "", last: currentUser.last_name || "" };
      }
      const name = currentUser?.name || localStorage.getItem("loggedInUserName");
      if (name) return splitName(name);
    }
    return null;
  });
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
  whiteSpace: "nowrap",
};

/**
 * Group chat header members: every member as a full firm user chat button when the title and all
 * the buttons fit on one line of the chat window header, otherwise `children` (the compact avatar
 * stack). `title` is the header title text, measured alongside the buttons so the check covers
 * the whole line.
 */
export default function GroupMembers({ group, title, children }) {
  const members = group?.group_member_avatars || [];
  const names = useMemberNames(members);
  const userOnlineStatus = useSelector((state) => state.chat2?.userOnlineStatus);
  const selfId = localStorage.getItem("loggedInUser");
  const allNamed = members.length > 0 && names.every(Boolean);
  const measureRef = useRef(null);
  const [fits, setFits] = useState(false);

  useLayoutEffect(() => {
    const measure = measureRef.current;
    // Found from our own node: a ref on the parent header isn't attached yet when this runs.
    const header = measure?.closest(".chat-window-header");
    if (!allNamed || !header) {
      setFits(false);
      return undefined;
    }
    const available = () => {
      const style = getComputedStyle(header);
      return header.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    };
    const check = () => setFits(measure.offsetWidth <= available());
    check();
    const observer = new ResizeObserver(check);
    observer.observe(header);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [allNamed]);

  const buttons = (measuring) =>
    members.map((member, index) => {
      const isSelf = sameId(member?.id, selfId);
      return (
        <UserChatBubble
          key={member?.id ?? index}
          userId={member?.id}
          first_name={names[index]?.first || ""}
          last_name={names[index]?.last || ""}
          profilePic={member?.avatar_url || ""}
          isActive={Boolean(userOnlineStatus?.[member?.id]?.isOnline)}
          width={19}
          equalizeNameWidths={false}
          // You can't chat with yourself, and the hidden measuring copy must not be clickable.
          hideIcon={true}
          disableChat={isSelf || measuring}
        />
      );
    });

  return (
    <>
      {allNamed && (
        // Off-screen copy of the full line (title + buttons) at its natural width.
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            overflow: "hidden",
            visibility: "hidden",
          }}
        >
          <div
            ref={measureRef}
            style={{ ...rowStyle, width: "max-content", fontSize: "14px", fontWeight: 600 }}
          >
            <span>{title}</span>
            {buttons(true)}
          </div>
        </div>
      )}
      {fits ? <div style={{ ...rowStyle, flexShrink: 0 }}>{buttons(false)}</div> : children}
    </>
  );
}
